import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Unlock } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import DataTable from '@/components/ui/DataTable';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Input from '@/components/ui/Input';
import Avatar from '@/components/ui/Avatar';
import Modal from '@/components/ui/Modal';
import UserDetail from './UserDetail';
import Toolbar from '@/components/ui/Toolbar';

const ROLE_FILTER = (t) => [
  { value: '', label: t('users.filter.all') },
  { value: 'admin', label: t('users.filter.admin') },
  { value: 'trainer', label: t('users.filter.trainer') },
  { value: 'student', label: t('users.filter.student') },
];

export default function AdminUsers() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (role) params.role = role;
    if (q) params.q = q;
    api
      .get('/users', params)
      .then((res) => setRows((res.data || res).users || res.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, q]);

  const toggleBlock = async (u) => {
    const blocked = !!u.blockedAt;
    setBusy(u.id);
    try {
      await api.post(`/users/${u.id}/${blocked ? 'unblock' : 'block'}`);
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(null);
    }
  };

  const columns = [
    {
      key: 'name',
      header: t('users.column.user'),
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar name={r.name} src={r.avatar} size={34} />
          <div>
            <div style={{ fontWeight: 600 }}>{r.name || '—'}</div>
            <div style={{ color: 'var(--low)', fontSize: 12 }}>{r.email}</div>
          </div>
        </div>
      ),
    },
    { key: 'role', header: t('users.column.role'), render: (r) => <Badge variant="accent">{t(`roles.${r.role}`, r.role)}</Badge> },
    {
      key: 'status',
      header: t('screens.status'),
      render: (r) => {
        const blocked = !!r.blockedAt;
        return <Badge variant={blocked ? 'danger' : 'success'}>{blocked ? t('users.blocked') : t('users.active')}</Badge>;
      },
    },
    {
      key: 'actions',
      header: t('screens.actions'),
      align: 'right',
      render: (r) => {
        const blocked = !!r.blockedAt;
        return (
          <Button
            size="sm"
            variant={blocked ? 'outline' : 'danger'}
            icon={blocked ? Unlock : Lock}
            loading={busy === r.id}
            onClick={(e) => { e.stopPropagation(); toggleBlock(r); }}
          >
            {blocked ? t('screens.unblock') : t('screens.block')}
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader kicker="Movez · Admin" title={t('nav.users')} />
      <Toolbar>
        <Input
          placeholder={t('common.search')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ minWidth: 240 }}
        />
        <Select value={role} onChange={(e) => setRole(e.target.value)} options={ROLE_FILTER(t)} />
      </Toolbar>
      <DataTable columns={columns} rows={rows} loading={loading} onRowClick={(r) => setDetailId(r.id)} />

      <Modal open={!!detailId} onClose={() => setDetailId(null)} kicker={t('userDetail.kicker')} title={t('userDetail.title')} size="md">
        {detailId && <UserDetail userId={detailId} />}
      </Modal>
    </>
  );
}
